// ── Source-doc QA (Tier 1: algorithmic) ─────────────────────────────────────
// Cheap, deterministic checks run at read time on Pass 2 / Pass 3 generated
// source docs. Flags likely-poor-quality docs before downstream extraction.

export type DocStyle = 'pedagogy' | 'article'

export interface Tier1Scores {
  wordCount: number
  fleschKincaidGrade: number
  keyTermCoverage: number // fraction 0-1 of standard's key terms appearing in doc
  styleFit: 'match' | 'drift' | 'unclear'
  styleReason: string
  flags: string[] // human-readable issues worth flagging
}

export interface Tier2RubricScores {
  content?: number // 1-5
  completeness?: number
  grade_fit?: number
  style?: number
  composite?: number
  rationale?: string
  flags?: string[]
}

// Pedagogy markers — expected in lesson plans, NOT in domain articles.
const PEDAGOGY_MARKERS = [
  /\bstudents?\s+will\b/i,
  /\bteacher\b/i,
  /\blesson\b/i,
  /\bactivit(?:y|ies)\b/i,
  /\bassessment\b/i,
  /\bworksheets?\b/i,
  /\bminutes?\b/i,
  /\bwarm[- ]up\b/i,
  /\bclosur[ae]\b/i,
  /\bengage\b/i,
  /\bmanipulat(?:ive|ion)s?\b/i,
]

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

// Syllable-count approximation for Flesch-Kincaid.
function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, '')
  if (!w) return 0
  // Rough heuristic: count vowel groups
  const groups = w.match(/[aeiouy]+/g)
  let syllables = groups ? groups.length : 1
  if (w.length > 3 && w.endsWith('e')) syllables = Math.max(1, syllables - 1)
  return Math.max(1, syllables)
}

function fleschKincaidGrade(text: string): number {
  const sentences = text.split(/[.!?]+\s+/).filter((s) => s.trim().length > 0)
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (sentences.length === 0 || words.length === 0) return 0
  const syllables = words.reduce((acc, w) => acc + countSyllables(w), 0)
  const asl = words.length / sentences.length
  const asw = syllables / words.length
  return 0.39 * asl + 11.8 * asw - 15.59
}

// Extract "key terms" — multi-word noun-phrase-like spans from standard text
// (naive: capitalized or quoted phrases, plus key nouns identified heuristically).
// Good enough for a coverage check; not a production NLP pipeline.
function extractKeyTerms(standardText: string): string[] {
  if (!standardText) return []
  // Remove common filler
  const cleaned = standardText
    .replace(/[.,;:"'()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const words = cleaned.split(/\s+/).filter((w) => w.length > 3)
  // Dedupe, lowercased, keep nouns-ish (filter stop-words)
  const stop = new Set([
    'about',
    'above',
    'after',
    'again',
    'between',
    'other',
    'their',
    'there',
    'these',
    'those',
    'through',
    'using',
    'which',
    'while',
    'with',
    'within',
    'without',
    'would',
    'should',
    'could',
    'include',
    'includes',
    'based',
    'known',
    'multiple',
    'various',
  ])
  const terms = new Set<string>()
  for (const w of words) {
    const lc = w.toLowerCase()
    if (stop.has(lc)) continue
    terms.add(lc)
  }
  return Array.from(terms)
}

function keyTermCoverage(doc: string, standardText: string): number {
  const terms = extractKeyTerms(standardText)
  if (terms.length === 0) return 1
  const lc = doc.toLowerCase()
  const found = terms.filter((t) => lc.includes(t)).length
  return found / terms.length
}

function styleFit(doc: string, expected: DocStyle): { fit: 'match' | 'drift' | 'unclear'; reason: string } {
  const count = PEDAGOGY_MARKERS.reduce((acc, rx) => acc + (rx.test(doc) ? 1 : 0), 0)
  if (expected === 'pedagogy') {
    if (count >= 3) return { fit: 'match', reason: `Contains ${count} pedagogy markers — typical of a lesson plan.` }
    if (count === 0)
      return {
        fit: 'drift',
        reason: 'No pedagogy markers found. Lesson plan should include activities, assessment, etc.',
      }
    return { fit: 'unclear', reason: `Only ${count} pedagogy marker(s) — lighter than expected for a lesson plan.` }
  }
  // expected === 'article'
  if (count === 0) return { fit: 'match', reason: 'No pedagogy markers — consistent with a reference article.' }
  if (count >= 3)
    return {
      fit: 'drift',
      reason: `Contains ${count} pedagogy markers — article has drifted into lesson-plan territory.`,
    }
  return { fit: 'unclear', reason: `${count} pedagogy marker(s) present — mild drift.` }
}

export function computeTier1(
  doc: string,
  standardText: string,
  expectedStyle: DocStyle,
  targetGrade?: string,
): Tier1Scores {
  const wc = wordCount(doc)
  const grade = fleschKincaidGrade(doc)
  const coverage = keyTermCoverage(doc, standardText)
  const style = styleFit(doc, expectedStyle)
  const flags: string[] = []

  const minWords = expectedStyle === 'pedagogy' ? 300 : 400
  const maxWords = 800
  if (wc < minWords) flags.push(`Short: ${wc} words (expected ≥ ${minWords})`)
  if (wc > maxWords) flags.push(`Long: ${wc} words (expected ≤ ${maxWords})`)

  // Grade-level check — only if targetGrade is numeric
  if (targetGrade) {
    const tg = parseInt(String(targetGrade).replace(/\D/g, ''), 10)
    if (Number.isFinite(tg) && Math.abs(grade - tg) > 2.5) {
      flags.push(`Grade-level drift: FK=${grade.toFixed(1)} vs target grade ${tg}`)
    }
  }

  if (coverage < 0.5) flags.push(`Low key-term coverage: ${(coverage * 100).toFixed(0)}% of standard terms appear`)
  if (style.fit === 'drift') flags.push(style.reason)

  return {
    wordCount: wc,
    fleschKincaidGrade: grade,
    keyTermCoverage: coverage,
    styleFit: style.fit,
    styleReason: style.reason,
    flags,
  }
}
